import { useAmp } from "next/amp"
import { Fragment, useEffect } from "react"
import { DEFAULT_LANG } from "@providers"
import { observer } from "mobx-react"
import classnames from "classnames"
import {
  Footer,
  Header,
  PureLogo,
  MainNavigation,
  Page,
  useStore,
} from "@features/common"
import {
  ApplicationBanner,
  Article,
  ArticleAuthor,
  JournalLogoEN,
  JournalLogoRU,
  JournalNavigation,
  RecommendationsCarousel,
} from "@features/journal"
import { Subscription } from "@features/subscription"
import { DatingLogo } from "@features/dating"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { ellipsisText } from "@lib/string"
import { IconHamburger } from "@lib/icons"
import { useSmartBanner } from "@features/smart-banner"
import { ArticleShare } from "@features/share"
import { modelsInit } from "@models"

import type { GetStaticProps, GetStaticPaths } from "next"
import type {
  CountryCodePathValues,
  SmartBannerPathValues,
  AMPPathValues,
} from "lib/isr"
import type { LanguageISO } from "@providers"

export const config = {
  amp: "hybrid",
}

type PostParsedUrlQuery = {
  lang?: LanguageISO
  slug: string
  countryCode: CountryCodePathValues
  smartBanner: SmartBannerPathValues
  ampValue: AMPPathValues
}

export const getStaticProps: GetStaticProps<{}, PostParsedUrlQuery> = async (
  context,
) => {
  const Models = modelsInit()
  const {
    slug = "",
    lang = "en",
    countryCode = "ru",
    ampValue = "full",
    smartBanner = "smart-banner-enabled",
  } = context.params || {}

  await Models.GlobalModel.getInitial()
  Models.GlobalModel.setPostFirstServerUrl({
    slug,
    lang,
    isAmp: ampValue === "amp",
  })

  // TODO: Maybe need to delete
  if (
    Models.GlobalModel.accessLanguages.findIndex(
      (accessLang) => accessLang === lang,
    ) === -1
  ) {
    if (Models.GlobalModel.accessLanguages.length > 0) {
      return {
        redirect: {
          destination: `/${DEFAULT_LANG}`,
          permanent: false,
        },
      }
    }
    return {
      props: Models.serialize(),
      revalidate: Number(process.env.PAGE_REVALIDATE_TIMEOUT) || 30,
    }
  }

  Models.GlobalModel.setLang(lang)

  try {
    Models.GlobalModel.setClientCountryCode(countryCode)
    await Models.BlogModel.getPost(slug as string)
    await Models.BlogModel.getAllCategories()
    await Models.BlogModel.readDefaultArticlesBanner()
    await Models.GlobalModel.getSmartBanner()

    if (smartBanner === "smart-banner-disabled") {
      Models.GlobalModel.setIsSmartBannerDisabled(true)
    }

    if (!Models.BlogModel.postDetails) {
      return {
        redirect: {
          permanent: false,
          destination: `/${lang}/journal`,
        },
      }
    } else {
      await Models.BlogModel.getRecommendedPosts(
        Models.BlogModel.postDetails.id,
      )
    }

    return {
      props: Models.serialize(),
      revalidate: Number(process.env.PAGE_REVALIDATE_TIMEOUT) || 30,
    }
  } catch (err) {
    console.log("GET STATIC PROPS POST ERROR", err) // eslint-disable-line
    return {
      redirect: {
        destination: `/${lang}/not-found`,
        permanent: false,
      },
    }
  }
}

export const getStaticPaths: GetStaticPaths<PostParsedUrlQuery> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  }
}

const ArticlePage = observer(function ArticlePage() {
  const {
    GlobalModel,
    BlogModel: { postDetails, relatedPostsBySlug, articleBanner, branch_links },
  } = useStore()
  const isAmp = useAmp()
  const { smartBanner } = useSmartBanner()

  useEffect(() => {
    document.querySelector("main")?.scrollTo({
      top: 0,
    })
    //eslint-disable-next-line
  }, [postDetails?.id])

  if (!postDetails) {
    return null
  }

  const pageDescription =
    postDetails.meta_description || ellipsisText(postDetails.description)
  const alternateLinks = relatedPostsBySlug.map(({ lang, slug }) => ({
    lang,
    url: `${GlobalModel.originUrl || ""}/${lang}/journal/${slug}`,
  }))

  const lastSectionBackground =
    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    postDetails.elements[postDetails.elements.length - 1]?.background || "white"

  const metaImage = postDetails.image_preview.url || postDetails.image_cover.url

  return (
    <Page
      meta={{
        title: postDetails.title,
        metaTitle: postDetails.meta_title || postDetails.title,
        description: pageDescription,
        metaImage,
        metaPublishedTime: postDetails.published_at,
        metaAuthor: postDetails.author?.name,
        metaAuthorImg: postDetails.author?.image,
        isArticle: true,
        alternateLinks,
      }}
      navigation={
        isAmp ? null : (
          <MainNavigation>
            <JournalNavigation />
          </MainNavigation>
        )
      }
      smartBanner={smartBanner}
      header={
        <Header
          theme="dark"
          navigationOpener={
            <button
              onClick={GlobalModel.openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
          languageSwitcher={null}
          logo={
            <RouterLink
              uri="/journal"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3"
            >
              <div className="flex space-x-2 tablet-m:space-x-4">
                <PureLogo className="text-white h-4 w-auto my-1 tablet:w-auto tablet-m:h-7 tablet:h-7 tablet-m:my-1.5" />
                {GlobalModel.isPureDating ? (
                  <DatingLogo />
                ) : (
                  <>
                    {GlobalModel.lang === "ru" ? (
                      <JournalLogoRU />
                    ) : (
                      <JournalLogoEN />
                    )}
                  </>
                )}
              </div>
            </RouterLink>
          }
        />
      }
      alternativeHeader
      footer={
        <Footer
          googlePlayLink={branch_links.main_footer_google_play}
          appStoreLink={branch_links.main_footer_app_store}
          appGalleryLink={branch_links.main_footer_app_gallery}
        />
      }
    >
      <Article article={postDetails} />
      <div
        className={classnames({
          "bg-white": lastSectionBackground === "white",
          "bg-black text-white": lastSectionBackground === "black",
          "bg-gray-50": lastSectionBackground === "gray",
        })}
      >
        {postDetails.author && <ArticleAuthor author={postDetails.author} />}
        <ArticleShare background={lastSectionBackground} />
      </div>
      {isAmp ? null : (
        <Fragment>
          <RecommendationsCarousel />
          {articleBanner ? (
            <div className="desktop:max-w-desktop-l desktop:pt-20 desktop:pb-10 desktop:px-20 desktop:mx-auto">
              <ApplicationBanner {...articleBanner} />
            </div>
          ) : null}
          <Subscription />
        </Fragment>
      )}
    </Page>
  )
})

export default ArticlePage
